(() => {
  const globalKey = '__cmLeatherGuideNavigation20260914';
  if (window[globalKey]) {
    window[globalKey].mountAll(document);
    return;
  }
  const selector = '[data-cm-leather-guide]';
  const instances = new WeakMap();
  const decodeHash = (hash) => {
    try {
      return hash.length > 1 ? decodeURIComponent(hash.slice(1)) : null;
    } catch {
      return null;
    }
  };
  const mount = (root) => {
    if (instances.has(root)) {
      instances.get(root).refresh();
      return;
    }
    const nav = root.querySelector('.cm-lg__jump');
    const details = nav?.querySelector('details');
    const summary = details?.querySelector('summary');
    const label = summary?.querySelector('.cm-lg__jump-label');
    if (!nav || !details || !summary || !label) return;
    const controller = new AbortController();
    const { signal } = controller;
    const anchors = [...nav.querySelectorAll('.cm-lg__jump-links a')];
    const chapters = [...root.querySelectorAll('.cm-lg__chapter')];
    const names = new Map(anchors.map((anchor) => [
      decodeHash(anchor.hash),
      anchor.querySelector('.cm-menu-name')?.textContent.trim() || anchor.textContent.trim(),
    ]));
    const openLabel = summary.dataset.openLabel || label.textContent.trim();
    const closeLabel = summary.dataset.closeLabel || openLabel;
    const desktop = matchMedia('(min-width: 1800px)');
    let preferredChapter = null;
    let header = null;
    let headerOffset = -1;
    let frame = 0;
    let navigationFrame = 0;
    let correctionFrame = 0;
    const schedule = () => {
      if (!frame && !signal.aborted) frame = requestAnimationFrame(update);
    };
    const headerObserver = new ResizeObserver(schedule);
    const measureHeader = () => {
      const currentHeader = document.querySelector('.shopify-section--header');
      if (currentHeader !== header) {
        headerObserver.disconnect();
        header = currentHeader;
        if (header) headerObserver.observe(header);
      }
      const nextOffset = Math.max(0, Math.ceil(header?.getBoundingClientRect().bottom || 0));
      if (nextOffset !== headerOffset) {
        headerOffset = nextOffset;
        root.style.setProperty('--cm-menu-header', `${headerOffset}px`);
      }
      return headerOffset;
    };
    const targetFromHash = (hash) => {
      const id = decodeHash(hash);
      const target = id && document.getElementById(id);
      return target && root.contains(target) ? target : null;
    };
    const update = () => {
      frame = 0;
      if (signal.aborted || !root.isConnected) return;
      const threshold = measureHeader() + (desktop.matches ? 80 : 142);
      const visible = chapters.filter((chapter) => {
        const box = chapter.getBoundingClientRect();
        return box.top <= threshold && box.bottom > threshold;
      });
      if (!visible.includes(preferredChapter)) preferredChapter = null;
      const active = preferredChapter || visible[0];
      anchors.forEach((anchor) => {
        if (active && decodeHash(anchor.hash) === active.id) anchor.setAttribute('aria-current', 'location');
        else anchor.removeAttribute('aria-current');
      });
      label.textContent = details.open && !desktop.matches
        ? closeLabel
        : (active && names.get(active.id)) || openLabel;
      summary.setAttribute('aria-expanded', String(details.open));
    };
    const scrollTopFor = (target) => Math.max(0,
      window.scrollY + target.getBoundingClientRect().top - measureHeader() - (desktop.matches ? 28 : 84));
    const queueNavigation = (target) => {
      cancelAnimationFrame(navigationFrame);
      cancelAnimationFrame(correctionFrame);
      navigationFrame = requestAnimationFrame(() => {
        navigationFrame = 0;
        if (signal.aborted || !target.isConnected) return;
        preferredChapter = target.matches('.cm-lg__chapter') ? target : null;
        const top = scrollTopFor(target);
        window.scrollTo({ top, behavior: 'instant' });
          correctionFrame = requestAnimationFrame(() => {
            correctionFrame = 0;
            if (!signal.aborted && target.isConnected) {
              const correctedTop = scrollTopFor(target);
              if (Math.abs(correctedTop - window.scrollY) > 1) window.scrollTo({ top: correctedTop, behavior: 'instant' });
              schedule();
            }
          });
      });
    };
    const alignHash = () => {
      const target = targetFromHash(location.hash);
      if (target) queueNavigation(target);
    };
    const syncMode = () => {
      details.open = desktop.matches;
      update();
    };
    desktop.addEventListener('change', syncMode, { signal });
    details.addEventListener('toggle', () => {
      if (desktop.matches && !details.open) details.open = true;
      schedule();
    }, { signal });
    summary.addEventListener('click', (event) => {
      if (desktop.matches) event.preventDefault();
    }, { signal });
    nav.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !desktop.matches && details.open) {
        details.open = false;
        summary.focus();
        schedule();
      }
    }, { signal });
    document.addEventListener('click', (event) => {
      if (!desktop.matches && details.open && !nav.contains(event.target)) details.open = false;
    }, { signal });
    root.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target instanceof Element ? event.target.closest('a') : null;
      if (!anchor || !root.contains(anchor) || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;
      if (!anchor.hash || anchor.origin !== location.origin || anchor.pathname !== location.pathname || anchor.search !== location.search) return;
      const target = targetFromHash(anchor.hash);
      if (!target) return;
      event.preventDefault();
      if (!desktop.matches) details.open = false;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      history.replaceState(null, '', anchor.hash);
      queueNavigation(target);
    }, { signal });
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    window.addEventListener('hashchange', alignHash, { signal });
    instances.set(root, {
      refresh: schedule,
      destroy: () => {
        controller.abort();
        headerObserver.disconnect();
        cancelAnimationFrame(frame);
        cancelAnimationFrame(navigationFrame);
        cancelAnimationFrame(correctionFrame);
        root.style.removeProperty('--cm-menu-header');
        instances.delete(root);
      },
    });
    syncMode();
    if (document.readyState === 'complete') alignHash();
    else window.addEventListener('load', alignHash, { once: true, signal });
  };
  const rootsWithin = (scope) => [
    ...(scope.matches?.(selector) ? [scope] : []),
    ...scope.querySelectorAll(selector),
  ];
  const mountAll = (scope) => rootsWithin(scope).forEach(mount);
  window[globalKey] = { mountAll };
  mountAll(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountAll(document), { once: true });
  }
  document.addEventListener('shopify:section:load', (event) => {
    mountAll(event.target);
    document.querySelectorAll(selector).forEach((root) => instances.get(root)?.refresh());
  });
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => instances.get(root)?.destroy());
  });
})();
